// @spec GHG-STR-013
'use client';

/**
 * GHG Integration Connectors list page.
 * Displays configured integration connectors for automatic GHG data collection.
 */

import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertCircle } from 'lucide-react';
import { useState } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useGHGOrganizationId } from '@/features/ghg/api/use-ghg-organization-id';
import { IntegrationConnectorForm, IntegrationConnectorList } from '@/features/ghg/components';
import type { GHGIntegrationConnector, GHGIntegrationConnectorDetail } from '@/features/ghg/types';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/ghg/integrations/')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.ghgIntegrationConnectors'),
  }),
  component: GHGIntegrationsPage,
});

/**
 * GHG Integration Connectors page.
 *
 * Allows users to:
 * - View list of configured connectors
 * - Create new connectors
 * - Edit existing connectors
 * - View connector details and sync history
 */
function GHGIntegrationsPage() {
  const { t } = useTranslation('ghg');
  const navigate = useNavigate();
  const { isLoaded: isOrgLoaded } = useOrganization();
  const orgId = useGHGOrganizationId();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingConnector, setEditingConnector] = useState<GHGIntegrationConnector | null>(null);

  // Handle create click - open create dialog
  const handleCreateClick = () => {
    setCreateDialogOpen(true);
  };

  // Handle edit click - navigate to detail page or open edit dialog
  const handleEditClick = (connector: GHGIntegrationConnector) => {
    setEditingConnector(connector);
  };

  // Handle view click - navigate to connector detail page
  const handleViewClick = (connector: GHGIntegrationConnector) => {
    void navigate({
      to: '/ghg/integrations/$connectorId',
      params: { connectorId: connector.id },
    });
  };

  // Handle dialog close
  const handleCreateDialogClose = () => {
    setCreateDialogOpen(false);
  };

  // Handle edit dialog close
  const handleEditDialogClose = () => {
    setEditingConnector(null);
  };

  // Handle form success
  const handleFormSuccess = () => {
    setCreateDialogOpen(false);
    setEditingConnector(null);
  };

  // Show skeleton while loading org info
  if (!isOrgLoaded) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-7xl space-y-6">
          <header>
            <Skeleton className="h-9 w-64" />
            <Skeleton className="mt-2 h-5 w-96" />
          </header>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  // No organization available
  if (!orgId) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-7xl space-y-6">
          <header>
            <h1 className="text-3xl font-semibold text-foreground">{t('integrationsPage.heading')}</h1>
            <p className="mt-1 text-muted-foreground">
              {t('integrationsPage.description')}
            </p>
          </header>
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('integrationsPage.noOrgSelected')}</AlertTitle>
            <AlertDescription>
              {t('integrationsPage.noOrgSelectedDesc')}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">{t('integrationsPage.heading')}</h1>
          <p className="mt-1 text-muted-foreground">
            {t('integrationsPage.description')}
          </p>
        </header>

        <IntegrationConnectorList
          onCreateClick={handleCreateClick}
          onEditClick={handleEditClick}
          onViewClick={handleViewClick}
        />
      </div>

      {/* Create Connector Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('integrationsPage.createDialogTitle')}</DialogTitle>
            <DialogDescription>
              {t('integrationsPage.createDialogDesc')}
            </DialogDescription>
          </DialogHeader>
          <IntegrationConnectorForm
            onSuccess={handleFormSuccess}
            onCancel={handleCreateDialogClose}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Connector Dialog */}
      <Dialog
        open={!!editingConnector}
        onOpenChange={(open) => {
          if (!open) handleEditDialogClose();
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('integrationsPage.editDialogTitle')}</DialogTitle>
            <DialogDescription>
              {t('integrationsPage.editDialogDesc')}
            </DialogDescription>
          </DialogHeader>
          {editingConnector && (
            <IntegrationConnectorForm
              connector={editingConnector as unknown as GHGIntegrationConnectorDetail}
              onSuccess={handleFormSuccess}
              onCancel={handleEditDialogClose}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
